import { NotifyHelper } from 'classes/helpers/notify.helper';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { MlbSportId } from 'lib_ts/enums/mlb-stats-api/base.enum';
import { MLB_TEAMS_DICT } from 'lib_ts/enums/mlb.enums';
import { Controller } from 'lib_ts/enums/routing/controllers';
import { GameDataRoutes } from 'lib_ts/enums/routing/routes/game-data';
import { IServerResponse } from 'lib_ts/interfaces/common/i-server-response';
import { IVideo } from 'lib_ts/interfaces/i-video';
import {
  IMlbGame,
  IMlbGameScore,
} from 'lib_ts/interfaces/mlb-stats-api/i-game';
import { IGenerateVideoRequest } from 'lib_ts/interfaces/mlb-stats-api/i-generate-video-request';
import { IMlbImportRequest } from 'lib_ts/interfaces/mlb-stats-api/i-import-request';
import { IMlbPitchExt } from 'lib_ts/interfaces/mlb-stats-api/i-pitch';
import { IMlbPlayer } from 'lib_ts/interfaces/mlb-stats-api/i-player';
import { IMlbTeam } from 'lib_ts/interfaces/mlb-stats-api/i-team';
import { MlbStatsSkeletalData } from 'lib_ts/interfaces/mlb-stats-api/raw-results/game-guids-biomechanics';
import { MlbStatsGameGuidVideo } from 'lib_ts/interfaces/mlb-stats-api/raw-results/game-guids-video';
import { BaseRESTService } from 'services/_base-rest.service';

interface ISeasonTally {
  season: number;
  total: number;
}

export class MlbStatsService extends BaseRESTService {
  private static instance: MlbStatsService;
  static getInstance(): MlbStatsService {
    if (!MlbStatsService.instance) {
      MlbStatsService.instance = new MlbStatsService();
    }

    return MlbStatsService.instance;
  }

  private constructor() {
    super({
      controller: Controller.MlbStats,
    });
  }

  async countGames(config: { sportId: MlbSportId }): Promise<ISeasonTally[]> {
    return await this.get({
      uri: GameDataRoutes.CountGames,
      params: {
        sportId: config.sportId,
      } as any,
    })
      .then((result: IServerResponse) => {
        if (!result.success) {
          NotifyHelper.warning({
            message_md: result.error ?? 'Failed to count games.',
          });
          return [];
        }

        return result.data as ISeasonTally[];
      })
      .catch((e) => {
        NotifyHelper.error({
          message_md: 'Failed to count games.',
        });
        console.error(e);
        return [];
      });
  }

  // id = _id from mlb-stats-games
  async getGamePlayers(
    game: IMlbGame,
    teams: IMlbTeam[]
  ): Promise<IMlbPlayer[]> {
    return await this.get({
      uri: `${GameDataRoutes.GetGamePlayers}/${game.gamePk}`,
    })
      .then((result: IServerResponse) => {
        if (!result.success) {
          NotifyHelper.warning({
            message_md: result.error ?? 'Failed to get players for game.',
          });
          return [];
        }

        const homeTeam = teams.find((m) => m.teamPk === game.home.teamPk);
        const awayTeam = teams.find((m) => m.teamPk === game.away.teamPk);

        const output = result.data as IMlbPlayer[];

        if (homeTeam && awayTeam) {
          for (const player of output) {
            const isHome = !!homeTeam.playerPks.includes(player.playerPk);
            const team = isHome ? homeTeam : awayTeam;

            player._mx_details = {
              isHome: isHome,
              isPitcher: false,
              isBatter: false,
            };

            player._mx_team_details = MLB_TEAMS_DICT[team.teamPk.toString()];
          }
        }

        return output;
      })
      .catch((e) => {
        NotifyHelper.error({
          message_md: 'Failed to get players for game.',
        });
        console.error(e);
        return [];
      });
  }

  async getGameScore(gamePk: number): Promise<IMlbGameScore | undefined> {
    return await this.get({
      uri: `${GameDataRoutes.GetGameScore}/${gamePk}`,
    })
      .then((result: IServerResponse) => {
        if (!result.success) {
          NotifyHelper.warning({
            message_md: result.error ?? 'Failed to get score for game.',
          });
          return undefined;
        }

        return result.data as IMlbGameScore;
      })
      .catch((e) => {
        NotifyHelper.error({
          message_md: 'Failed to get score for game.',
        });
        console.error(e);
        return undefined;
      });
  }

  async getPlayerPitches(config: {
    mode: 'pitcher' | 'hitter';
    player: IMlbPlayer;
  }): Promise<IMlbPitchExt[]> {
    return await this.post(
      {
        uri: GameDataRoutes.GetPlayerPitches,
        params: {
          role: config.mode,
        } as any,
      },
      config.player
    )
      .then((result: IServerResponse) => {
        if (!result.success) {
          NotifyHelper.warning({
            message_md: result.error ?? 'Failed to get pitches for player.',
          });
          return [];
        }

        return result.data as IMlbPitchExt[];
      })
      .catch((e) => {
        NotifyHelper.error({
          message_md: 'Failed to get pitches for player.',
        });
        console.error(e);
        return [];
      });
  }

  async getGamePitches(gamePk: number): Promise<IMlbPitchExt[]> {
    return await this.get({
      uri: `${GameDataRoutes.GetGamePitches}/${gamePk}`,
    })
      .then((result: IServerResponse) => {
        if (!result.success) {
          NotifyHelper.warning({
            message_md: result.error ?? 'Failed to get pitches for game.',
          });
          return [];
        }

        return result.data as IMlbPitchExt[];
      })
      .catch((e) => {
        NotifyHelper.error({
          message_md: 'Failed to get pitches for game.',
        });
        console.error(e);
        return [];
      });
  }

  async getPitches(config: {
    sportId: MlbSportId;
    season: number | string;
    gamePk?: number | string;
    pitcherPk?: number | string;
    batterPk?: number | string;
  }): Promise<IMlbPitchExt[]> {
    return await this.get({
      uri: GameDataRoutes.GetPitches,
      params: {
        sportId: config.sportId,
        season: config.season,
        gamePk: config.gamePk ?? -1,
        pitcherPk: config.pitcherPk ?? -1,
        batterPk: config.batterPk ?? -1,
      } as any,
    })
      .then((result: IServerResponse) => {
        if (!result.success) {
          NotifyHelper.warning({
            message_md: result.error ?? 'Failed to get pitches for game.',
          });
          return [];
        }

        const output = result.data as IMlbPitchExt[];

        // for checked context to work
        output.forEach((m) => (m._id = m.guid));

        return output;
      })
      .catch((e) => {
        NotifyHelper.error({
          message_md: 'Failed to get pitches for game.',
        });
        console.error(e);
        return [];
      });
  }

  async getGamePlayVideos(
    gamePk: number | string,
    guid: string
  ): Promise<MlbStatsGameGuidVideo | undefined> {
    return await this.get({
      uri: GameDataRoutes.GetPlayVideos,
      params: {
        gamePk: gamePk,
        guid: guid,
      } as any,
    })
      .then((result: IServerResponse) => {
        if (!result.success) {
          NotifyHelper.warning({
            message_md: result.error ?? 'Failed to get broadcast data.',
          });
          return;
        }

        return result.data as MlbStatsGameGuidVideo;
      })
      .catch((e) => {
        NotifyHelper.error({
          message_md: 'Failed to get broadcast data.',
        });
        console.error(e);
        return undefined;
      });
  }

  async getGamePlaySkeletalData(
    gamePk: number | string,
    guid: string
  ): Promise<MlbStatsSkeletalData | undefined> {
    return await this.get({
      uri: GameDataRoutes.GetPlaySkeletal,
      params: {
        gamePk: gamePk,
        guid: guid,
      } as any,
    })
      .then((result: IServerResponse) => {
        if (!result.success) {
          NotifyHelper.warning({
            message_md: result.error ?? 'Failed to get skeletal data.',
          });
          return undefined;
        }

        return result.data as MlbStatsSkeletalData;
      })
      .catch((e) => {
        NotifyHelper.error({
          message_md: 'Failed to get skeletal data.',
        });
        console.error(e);
        return undefined;
      });
  }

  async makeSkeletalVideo(
    gamePk: number | string,
    guid: string,
    options: IGenerateVideoRequest
  ): Promise<IVideo | undefined> {
    return await this.post(
      {
        uri: GameDataRoutes.GeneratePlaySkeletalVideo,
        params: {
          gamePk: gamePk,
          guid: guid,
        } as any,
      },
      options
    )
      .then((result: IServerResponse) => {
        if (!result.success) {
          NotifyHelper.warning({
            message_md: result.error ?? 'Failed to generate video.',
            delay_ms: 0,
          });
          return;
        }

        return result.data as IVideo;
      })
      .catch((e) => {
        NotifyHelper.error({
          message_md: 'Failed to generate video.',
        });

        console.error(e);
        return undefined;
      });
  }

  async getGames(config: {
    sportId: MlbSportId;
    season: number | string;
  }): Promise<IMlbGame[]> {
    return await this.get({
      uri: GameDataRoutes.GetGames,
      params: {
        sportId: config.sportId,
        season: config.season,
      } as any,
    })
      .then((result: IServerResponse) => {
        if (!result.success) {
          NotifyHelper.warning({
            message_md: result.error ?? 'Failed to get games.',
          });
          return [];
        }

        return result.data as IMlbGame[];
      })
      .catch((e) => {
        NotifyHelper.error({
          message_md: 'Failed to get games.',
        });
        console.error(e);
        return [];
      });
  }

  // sorts them by name
  async getPlayers(config: {
    sportId: MlbSportId;
    season: number | string;
    // for mixin details
    teams: IMlbTeam[];
  }): Promise<IMlbPlayer[]> {
    return await this.get({
      uri: GameDataRoutes.GetPlayers,
      params: {
        sportId: config.sportId,
        season: config.season,
      } as any,
    })
      .then((result: IServerResponse) => {
        if (!result.success) {
          NotifyHelper.warning({
            message_md: result.error ?? 'Failed to get players.',
          });
          return [];
        }

        const output = result.data as IMlbPlayer[];

        for (const player of output) {
          const team = config.teams.find((team) =>
            team.playerPks.includes(player.playerPk)
          );

          if (team) {
            player._mx_team_details = MLB_TEAMS_DICT[team.teamPk.toString()];
          }
        }

        output.sort((a, b) => StringHelper.safeSort(a.name, b.name));

        return output;
      })
      .catch((e) => {
        NotifyHelper.error({
          message_md: 'Failed to get players.',
        });
        console.error(e);
        return [];
      });
  }

  async getTeams(config: {
    sportId: MlbSportId;
    season: number | string;
  }): Promise<IMlbTeam[]> {
    return await this.get({
      uri: GameDataRoutes.GetTeams,
      params: {
        sportId: config.sportId,
        season: config.season,
      } as any,
    })
      .then((result: IServerResponse) => {
        if (!result.success) {
          NotifyHelper.warning({
            message_md: result.error ?? 'Failed to get teams.',
          });
          return [];
        }

        const output = result.data as IMlbTeam[];

        for (const team of output) {
          team._mx_details = MLB_TEAMS_DICT[team.teamPk.toString()];
        }

        output.sort((a, b) => StringHelper.safeSort(a.name, b.name));

        return output;
      })
      .catch((e) => {
        NotifyHelper.error({
          message_md: 'Failed to get teams.',
        });
        console.error(e);
        return [];
      });
  }

  async getCurrentTeams(config: { sportId: MlbSportId }): Promise<IMlbTeam[]> {
    return await this.get({
      uri: GameDataRoutes.GetCurrentTeams,
      params: {
        sportId: config.sportId,
      } as any,
    })
      .then((result: IServerResponse) => {
        if (!result.success) {
          NotifyHelper.warning({
            message_md: result.error ?? 'Failed to get current teams.',
          });
          return [];
        }

        const output = result.data as IMlbTeam[];

        for (const team of output) {
          team._mx_details = MLB_TEAMS_DICT[team.teamPk.toString()];
        }

        output.sort((a, b) => StringHelper.safeSort(a.name, b.name));

        return output;
      })
      .catch((e) => {
        NotifyHelper.error({
          message_md: 'Failed to get current teams.',
        });
        console.error(e);
        return [];
      });
  }

  async importRequest(payload: IMlbImportRequest): Promise<boolean> {
    return await this.post(
      {
        uri: GameDataRoutes.ImportRequest,
      },
      payload
    )
      .then((result: IServerResponse) => {
        if (!result.success) {
          NotifyHelper.warning({
            message_md: result.error ?? 'Failed to process import request.',
          });

          return false;
        }

        return true;
      })
      .catch((e) => {
        NotifyHelper.error({
          message_md: 'Failed to process import request.',
        });
        console.error(e);
        return false;
      });
  }
}
